import { useState } from 'react'
import { LineChart, TrendingUp, TrendingDown, Lock, Sparkles, TrendingDown as DrawdownIcon } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useHome } from '@/hooks/useHome'
import { usePersonalBankroll, usePersonalBankrollHistory } from '@/hooks/usePersonalBankroll'
import { useStrategiesComparator } from '@/hooks/useStrategies'
import { useMyEvolution, useMyStrategyPerformance, useMyIntelligence } from '@/hooks/useBlackIntelligence'
import { EvolutionChart } from '@/components/EvolutionChart'
import { UpgradePrompt } from '@/components/UpgradePrompt'
import { SkeletonList } from '@/components/LoadingSkeleton'
import { EmptyState } from '@/components/EmptyState'
import { cn } from '@/lib/utils'
import type { TimeWindow } from '@/types/strategies'

function formatMoney(value: number | undefined | null): string {
  if (value == null) return 'N/D'
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value)
}

const WINDOWS: { key: TimeWindow; label: string }[] = [
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: '90d', label: '90d' },
  { key: 'total', label: 'Total' },
]

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-panel-elevated/30 p-1.5 rounded-lg">
      <span className="text-[8px] font-bold text-text-dim block">{label}</span>
      <span className="font-black text-text">{value}</span>
    </div>
  )
}

/** BLACK discovery teaser -- un único bloque, un único CTA (Sección 29 del encargo: "no
 * mostrar 10 candados"). Nunca datos ficticios, nunca gráficos fake. */
function BlackDiscoveryTeaser({ title, description }: { title: string; description: string }) {
  return (
    <div className="card text-center py-8 space-y-3 border-exact/20">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-exact-soft/30 mx-auto">
        <Lock className="h-5 w-5 text-exact" />
      </span>
      <div>
        <p className="text-sm font-semibold text-text">{title}</p>
        <p className="text-xs text-text-muted mt-1 max-w-[30ch] mx-auto leading-relaxed">{description}</p>
      </div>
      <a
        href="/mas/suscripcion"
        className="inline-block px-4 py-2 rounded-lg bg-exact text-white text-sm font-semibold"
      >
        Ver BLACK
      </a>
    </div>
  )
}

/** Tab "Resumen" -- 100% personal, EXCLUSIVAMENTE `PersonalBet` liquidadas
 * (`PersonalBankrollService.compute_metrics`). Nunca mezcla con el rendimiento oficial de
 * John Anthony. Disponible en PRO y BLACK (Sección 15: PRO conserva valor real). */
function SummaryTab() {
  const { data: bankroll, isLoading } = usePersonalBankroll()
  const { data: history } = usePersonalBankrollHistory()

  if (isLoading) return <SkeletonList count={2} />

  if (!bankroll?.configured) {
    return (
      <EmptyState
        icon={LineChart}
        title="Todavía no tienes datos personales"
        body='Configura "Mi Banca" y marca "LA JUGUÉ" en tus primeras recomendaciones para ver tu evolución real aquí.'
      />
    )
  }

  const bettingProfitPositive = (bankroll.betting_profit_loss ?? 0) >= 0
  const evolutionPoints = (history ?? []).slice().reverse().map((t) => t.balance_after)

  return (
    <div className="space-y-4 md:grid md:grid-cols-5 md:gap-4 md:space-y-0">
      <div className="card p-4 rounded-2xl space-y-4 md:col-span-2">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-panel-elevated/50 p-3 rounded-xl border border-border/30 text-center">
            <p className="text-[9px] font-bold uppercase text-text-dim">P/L Deportivo</p>
            <p className={cn('text-base font-mono font-black flex items-center justify-center gap-1', bettingProfitPositive ? 'text-success' : 'text-danger')}>
              {bettingProfitPositive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
              {formatMoney(bankroll.betting_profit_loss)}
            </p>
          </div>
          <div className="bg-panel-elevated/50 p-3 rounded-xl border border-border/30 text-center">
            <p className="text-[9px] font-bold uppercase text-text-dim">ROI</p>
            <p className="text-base font-mono font-black text-text">{bankroll.roi != null ? `${bankroll.roi.toFixed(2)}%` : 'N/D'}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 pt-1 border-t border-border/30">
          <div className="text-center">
            <p className="text-[9px] font-bold uppercase text-text-dim">Win Rate</p>
            <p className="text-sm font-mono font-black text-text">{bankroll.win_rate != null ? `${bankroll.win_rate.toFixed(1)}%` : 'N/D'}</p>
          </div>
          <div className="text-center">
            <p className="text-[9px] font-bold uppercase text-text-dim">Apostado total</p>
            <p className="text-sm font-mono font-black text-text">{formatMoney(bankroll.total_staked)}</p>
          </div>
          <div className="text-center">
            <p className="text-[9px] font-bold uppercase text-text-dim">Cuota media</p>
            <p className="text-sm font-mono font-black text-text">{bankroll.average_odds_played != null ? `@${bankroll.average_odds_played.toFixed(2)}` : 'N/D'}</p>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3 pt-1 text-[10px] font-bold text-text-dim">
          <span className="text-success">{bankroll.won ?? 0} ganadas</span>
          <span className="text-danger">{bankroll.lost ?? 0} perdidas</span>
          <span>{bankroll.void ?? 0} void</span>
          <span>{bankroll.pending ?? 0} pendientes</span>
        </div>
      </div>

      <div className="card p-4 rounded-2xl space-y-2 md:col-span-3">
        <p className="text-[10px] font-black uppercase tracking-wider text-text-dim">Evolución de tu banca</p>
        <EvolutionChart points={evolutionPoints} />
        <p className="text-[9px] text-text-dim leading-relaxed pt-1">
          Estos números salen exclusivamente de tus apuestas registradas con "LA JUGUÉ" -- nunca de
          una simulación ni del rendimiento oficial de John Anthony.
        </p>
      </div>
    </div>
  )
}

/** Tab "Estrategias" -- comparador OFICIAL (PRO/BLACK, sin cambios de fuente) + comparador
 * PERSONAL (BLACK, Sección 24 del encargo: "SU PROPIO historial", nunca resultados oficiales
 * como si el usuario hubiera jugado todo). */
function StrategiesTab({ hasPersonalComparator }: { hasPersonalComparator: boolean }) {
  const [source, setSource] = useState<'oficial' | 'personal'>('oficial')
  const [window, setWindow] = useState<TimeWindow>('total')
  const { data: official, isLoading: officialLoading } = useStrategiesComparator(window)
  const { data: personal, isLoading: personalLoading } = useMyStrategyPerformance()

  return (
    <div className="space-y-3">
      <div className="flex gap-1.5 bg-panel-elevated/40 border border-border p-1 rounded-xl">
        <button
          onClick={() => setSource('oficial')}
          className={cn('flex-1 text-center py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition', source === 'oficial' ? 'bg-brand text-black' : 'text-text-dim hover:text-text')}
        >
          Oficial John Anthony
        </button>
        <button
          onClick={() => setSource('personal')}
          className={cn('flex-1 text-center py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition flex items-center justify-center gap-1', source === 'personal' ? 'bg-brand text-black' : 'text-text-dim hover:text-text')}
        >
          {!hasPersonalComparator && <Lock className="h-3 w-3" />}
          Mi historial personal
        </button>
      </div>

      {source === 'oficial' && (
        <div className="space-y-3">
          <p className="text-[10px] text-text-dim leading-relaxed px-1">
            Rendimiento OFICIAL de cada estrategia de John Anthony -- no es tu rendimiento personal.
          </p>
          <div className="flex gap-1.5">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                onClick={() => setWindow(w.key)}
                className={`px-2.5 py-1 rounded-lg text-2xs font-bold uppercase border ${
                  window === w.key ? 'bg-brand text-black border-brand' : 'bg-panel-elevated/40 text-text-dim border-border/50'
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          {officialLoading && <SkeletonList count={2} />}
          <div className="space-y-2">
            {official?.strategies.map((row) => {
              const insufficient = row.sample_status === 'insuficiente'
              return (
                <div key={row.strategy_key} className="card p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-text text-sm">
                      <span className="mr-1.5" aria-hidden>{row.icon}</span>
                      {row.label}
                    </span>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full border ${insufficient ? 'bg-panel-elevated text-text-dim border-border/50' : 'bg-emerald-950/40 text-emerald-400 border-emerald-500/40'}`}>
                      {insufficient ? 'Muestra insuficiente' : 'OK'}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center text-2xs font-mono">
                    <MiniStat label="Picks" value={String(row.picks)} />
                    <MiniStat label="Win Rate" value={!insufficient && row.win_rate != null ? `${row.win_rate.toFixed(1)}%` : 'N/D'} />
                    <MiniStat label="ROI" value={!insufficient && row.roi != null ? `${row.roi >= 0 ? '+' : ''}${row.roi.toFixed(1)}%` : 'N/D'} />
                    <MiniStat label="Cuota Med." value={!insufficient && row.average_odds != null ? `@${row.average_odds.toFixed(2)}` : 'N/D'} />
                  </div>
                </div>
              )
            })}
          </div>
          <p className="text-[9px] text-text-dim text-center">Umbral mínimo de muestra: {official?.min_sample_size ?? 10} picks decididos.</p>
        </div>
      )}

      {source === 'personal' && !hasPersonalComparator && (
        <BlackDiscoveryTeaser
          title="Compara tus propias estrategias"
          description="BLACK compara tu historial real por categoría -- Top IA, Top Seguro, Parlays, Reto -- nunca resultados oficiales como si fueran tuyos."
        />
      )}

      {source === 'personal' && hasPersonalComparator && (
        <div className="space-y-3">
          <p className="text-[10px] text-text-dim leading-relaxed px-1">
            Basado en tus apuestas registradas -- nunca el rendimiento oficial de John Anthony.
          </p>
          {personalLoading && <SkeletonList count={2} />}
          {personal && !personal.configured && (
            <EmptyState icon={LineChart} title="Sin apuestas registradas todavía" body='Marca "LA JUGUÉ" para empezar a construir tu comparador personal.' />
          )}
          <div className="space-y-2">
            {personal?.strategies.map((row) => {
              const insufficient = row.sample_status === 'insuficiente'
              return (
                <div key={row.strategy_key} className="card p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-text text-sm">{row.label}</span>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full border ${insufficient ? 'bg-panel-elevated text-text-dim border-border/50' : 'bg-emerald-950/40 text-emerald-400 border-emerald-500/40'}`}>
                      {insufficient ? 'Muestra insuficiente' : 'OK'}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center text-2xs font-mono">
                    <MiniStat label="Apuestas" value={String(row.picks)} />
                    <MiniStat label="Win Rate" value={!insufficient && row.win_rate != null ? `${row.win_rate.toFixed(1)}%` : 'N/D'} />
                    <MiniStat label="ROI" value={!insufficient && row.roi != null ? `${row.roi >= 0 ? '+' : ''}${row.roi.toFixed(1)}%` : 'N/D'} />
                    <MiniStat label="Cuota Med." value={!insufficient && row.average_odds != null ? `@${row.average_odds.toFixed(2)}` : 'N/D'} />
                  </div>
                </div>
              )
            })}
          </div>
          <p className="text-[9px] text-text-dim text-center">Umbral mínimo de muestra: {personal?.min_sample_size ?? 10} apuestas decididas.</p>
        </div>
      )}
    </div>
  )
}

/** Tab "Análisis Avanzado" -- Advanced Personal Evolution (Sección 22/23 del encargo). BLACK
 * exclusivo. Progressive disclosure: categoría -> rango de cuota -> drawdown, nunca las 3
 * simultáneas sin jerarquía. */
function AdvancedEvolutionTab({ hasAccess }: { hasAccess: boolean }) {
  const { data, isLoading } = useMyEvolution(undefined)

  if (!hasAccess) {
    return (
      <BlackDiscoveryTeaser
        title="Análisis avanzado de tu comportamiento"
        description="Dónde te ha ido mejor, en qué rango de cuota, y cuál fue tu mayor drawdown -- todo calculado desde tu historial real."
      />
    )
  }

  if (isLoading) return <SkeletonList count={2} />

  if (!data?.configured) {
    return <EmptyState icon={LineChart} title="Sin historial suficiente todavía" body='Marca "LA JUGUÉ" en tus recomendaciones para desbloquear este análisis.' />
  }

  return (
    <div className="space-y-4">
      {data.drawdown && (
        <div className="card p-4 rounded-2xl space-y-1">
          <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-text-dim">
            <DrawdownIcon className="h-3.5 w-3.5" /> Drawdown personal
          </div>
          <p className="text-xl font-mono font-black text-danger">
            {data.drawdown.max_drawdown_pct != null ? `${data.drawdown.max_drawdown_pct.toFixed(1)}%` : 'N/D'}
          </p>
          <p className="text-[10px] text-text-dim">
            Caída máxima desde tu punto más alto ({formatMoney(data.drawdown.max_drawdown_amount)}), sobre {data.drawdown.sample_size} apuestas decididas.
          </p>
        </div>
      )}

      <section className="space-y-2">
        <p className="text-[10px] font-black uppercase tracking-wider text-text-dim px-1">Por categoría</p>
        <div className="space-y-2">
          {data.by_category.map((c) => (
            <div key={c.category} className="card p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-bold text-text text-sm">{c.label}</span>
                <span className={`text-[9px] px-2 py-0.5 rounded-full border ${c.sample_status === 'insuficiente' ? 'bg-panel-elevated text-text-dim border-border/50' : 'bg-emerald-950/40 text-emerald-400 border-emerald-500/40'}`}>
                  {c.sample_status === 'insuficiente' ? 'Muestra insuficiente' : 'OK'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-2xs font-mono">
                <MiniStat label="Apuestas" value={String(c.decided)} />
                <MiniStat label="Win Rate" value={c.win_rate != null ? `${c.win_rate.toFixed(1)}%` : 'N/D'} />
                <MiniStat label="ROI" value={c.roi != null ? `${c.roi >= 0 ? '+' : ''}${c.roi.toFixed(1)}%` : 'N/D'} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {data.by_odds_range.length > 0 && (
        <section className="space-y-2">
          <p className="text-[10px] font-black uppercase tracking-wider text-text-dim px-1">Por rango de cuota</p>
          <div className="grid grid-cols-2 gap-2">
            {data.by_odds_range.map((r) => (
              <div key={r.range} className="card p-3 space-y-1.5">
                <p className="font-bold text-text text-xs">@{r.range}</p>
                <div className="grid grid-cols-2 gap-1.5 text-center text-2xs font-mono">
                  <MiniStat label="Win Rate" value={r.win_rate != null ? `${r.win_rate.toFixed(1)}%` : 'N/D'} />
                  <MiniStat label="ROI" value={r.roi != null ? `${r.roi >= 0 ? '+' : ''}${r.roi.toFixed(1)}%` : 'N/D'} />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <p className="text-[9px] text-text-dim text-center">Umbral mínimo de muestra: {data.min_sample_size} apuestas decididas.</p>
    </div>
  )
}

/** Tab "Inteligencia" -- Personal Insight Engine (Sección 25-27 del encargo). BLACK
 * exclusivo. Cada insight es respaldado, nunca inventado; sin muestra suficiente, el bloque
 * queda vacío con un mensaje humano, nunca "0%"/"NaN". */
function IntelligenceTab({ hasAccess }: { hasAccess: boolean }) {
  const { data, isLoading } = useMyIntelligence()

  if (!hasAccess) {
    return (
      <BlackDiscoveryTeaser
        title="Inteligencia personal con BLACK"
        description="Insights cortos, respaldados por tu propio historial -- nunca inventados, nunca genéricos."
      />
    )
  }

  if (isLoading) return <SkeletonList count={2} />

  if (!data?.configured || data.insights.length === 0) {
    // P0.56 (Sección 12/18 del encargo): nunca "sin datos" a secas -- progreso real hacia
    // el umbral (mismo umbral central 10 que en el teaser del Home, Sección 6) más una
    // explicación honesta de qué se va a aprender cuando haya muestra suficiente.
    const totalBets = data?.total_bets ?? 0
    const threshold = 10
    return (
      <div className="card p-5 space-y-4">
        <div className="text-center space-y-2">
          <Sparkles className="h-8 w-8 mx-auto text-exact/60" />
          <p className="text-sm font-semibold text-text">John Anthony está construyendo tu historial personal</p>
          <p className="text-xs text-text-muted max-w-[36ch] mx-auto leading-relaxed">
            Registra tus apuestas con "LA JUGUÉ" para desbloquear este análisis.
          </p>
        </div>
        <div className="space-y-1 max-w-xs mx-auto w-full">
          <div className="flex items-center justify-between text-[10px] font-bold text-text-dim">
            <span className="figure">{Math.min(totalBets, threshold)} / {threshold} apuestas registradas</span>
          </div>
          <div className="h-1.5 rounded-full bg-panel-elevated overflow-hidden">
            <div
              className="h-full rounded-full bg-exact transition-all"
              style={{ width: `${Math.min(100, (totalBets / threshold) * 100)}%` }}
            />
          </div>
        </div>
        <div className="hairline pt-3 space-y-1.5">
          <p className="text-[9px] font-black uppercase tracking-wider text-text-dim text-center">Qué vas a descubrir</p>
          <ul className="text-[11px] text-text-muted leading-relaxed space-y-1 max-w-xs mx-auto">
            <li>· Tus mejores categorías de apuesta y en cuáles rindes peor</li>
            <li>· Cómo te comportas en distintos rangos de cuota</li>
            <li>· Tu desviación respecto a la referencia oficial de John Anthony</li>
            <li>· Concentración, drawdown y patrones propios de decisión</li>
          </ul>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {data.insights.map((insight, i) => (
        <details key={i} className="card p-4 space-y-1.5 group">
          <summary className="flex items-center justify-between cursor-pointer list-none">
            <span className="text-sm font-bold text-text">{insight.title}</span>
            <span className="text-[9px] text-text-dim uppercase font-bold group-open:hidden">Ver por qué</span>
          </summary>
          <p className="text-xs text-text-muted leading-relaxed pt-1">{insight.message}</p>
          <div className="pt-2 mt-2 border-t border-border/30 text-[10px] text-text-dim space-y-0.5">
            <p className="font-bold uppercase text-[9px] tracking-wider">Basado en</p>
            <p>{insight.sample_size} apuestas · {insight.period}</p>
          </div>
        </details>
      ))}
    </div>
  )
}

/** P0.55 (29/08/2026, Sección 14-27 del encargo): "Mi Evolución" V2 -- centro de inteligencia
 * personal. Progressive disclosure en 4 pestañas: Resumen (PRO+), Estrategias (oficial PRO+ /
 * personal BLACK), Análisis Avanzado (BLACK), Inteligencia (BLACK). Nunca 30 métricas juntas
 * en una sola pantalla. */
export function MyEvolutionPage() {
  const { user } = useAuth()
  const { data: homeData, isLoading } = useHome(true)
  const [tab, setTab] = useState<'resumen' | 'estrategias' | 'avanzado' | 'inteligencia'>('resumen')

  const isAdmin = user?.role === 'ADMIN'
  const hasBasicAccess = isAdmin || Boolean(homeData?.plan_features?.bankroll)
  const hasPersonalComparator = isAdmin || Boolean(homeData?.plan_features?.personal_strategy_comparator)
  const hasAdvancedEvolution = isAdmin || Boolean(homeData?.plan_features?.advanced_evolution)
  const hasPersonalIntelligence = isAdmin || Boolean(homeData?.plan_features?.personal_intelligence)

  const TABS: { key: typeof tab; label: string; locked: boolean }[] = [
    { key: 'resumen', label: 'Resumen', locked: false },
    { key: 'estrategias', label: 'Estrategias', locked: false },
    { key: 'avanzado', label: 'Análisis', locked: !hasAdvancedEvolution },
    { key: 'inteligencia', label: 'Inteligencia', locked: !hasPersonalIntelligence },
  ]

  return (
    <div className="p-4 space-y-4 md:max-w-4xl">
      <div className="space-y-1">
        <h1 className="text-lg font-black text-text flex items-center gap-2">
          <LineChart className="h-5 w-5 text-brand" /> Mi Evolución
        </h1>
        <p className="text-xs text-text-dim">Cómo estás usando las recomendaciones de John Anthony -- tu rendimiento real.</p>
      </div>

      {isLoading && <SkeletonList count={2} />}

      {!isLoading && !hasBasicAccess && (
        <UpgradePrompt requiredPlan="PRO" feature="Mi Evolución" trialExpired={homeData?.trial_expired} />
      )}

      {!isLoading && hasBasicAccess && (
        <>
          <div className="flex gap-1.5 bg-panel-elevated/40 border border-border p-1 rounded-xl overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={cn(
                  'flex-1 shrink-0 flex items-center justify-center gap-1 text-center py-1.5 px-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition',
                  tab === t.key ? 'bg-brand text-black' : 'text-text-dim hover:text-text'
                )}
              >
                {t.locked && <Lock className="h-2.5 w-2.5" />}
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'resumen' && <SummaryTab />}
          {tab === 'estrategias' && <StrategiesTab hasPersonalComparator={hasPersonalComparator} />}
          {tab === 'avanzado' && <AdvancedEvolutionTab hasAccess={hasAdvancedEvolution} />}
          {tab === 'inteligencia' && <IntelligenceTab hasAccess={hasPersonalIntelligence} />}
        </>
      )}
    </div>
  )
}
